import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { auth } from "../firebaseConfig";
import { onAuthStateChanged } from "firebase/auth";
import { checkAccess } from "../accessControl";
import { useDarkMode } from "../DarkModeContext";
import { CheckCircle2, ShoppingBag } from "lucide-react";
import { loadProfileHybrid } from "../userDataSync";
import Header from "../components/Header";
import TabBar from "../components/TabBar";
import OfflineDownloadButton from "../OfflineDownloadButton";

/* -------------------- ANIMATIONS -------------------- */
const animations = `
@keyframes fadeSlideIn {
  from { opacity: 0; transform: translate(-50%, 30px); }
  to { opacity: 1; transform: translate(-50%, 0); }
}
.animate-fade-slide-in {
  animation: fadeSlideIn 0.5s ease-out forwards;
}

@keyframes fadeOut {
  from { opacity: 0; transform: scale(1) blur(0px); }
  to { opacity: 1; transform: scale(1.02) blur(2px); }
}
.animate-fade-out {
  animation: fadeOut 0.5s ease-in forwards;
}
`;

/* -------------------- INSTALL PROMPT -------------------- */
function InstallPrompt() {
  const [showPrompt, setShowPrompt] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (window.navigator.standalone) return;
    const ua = window.navigator.userAgent.toLowerCase();
    const isIOS = /iphone|ipad|ipod/.test(ua);
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    if (isIOS && isSafari) {
      const timer = setTimeout(() => setShowPrompt(true), 5000);
      return () => clearTimeout(timer);
    }
  }, []);

  if (!showPrompt || dismissed) return null;

  return (
    <div
      className="fixed bottom-24 left-1/2 -translate-x-1/2
                 backdrop-blur-xl px-4 py-2.5 rounded-2xl
                 text-sm shadow-md w-[90%] max-w-sm text-center
                 z-50 animate-fade-slide-in"
      style={{
        backgroundColor: 'rgba(255, 250, 223, 0.95)',
        color: '#413C04',
        border: '1px solid #E4B4A6'
      }}
    >
      <button
        onClick={() => setDismissed(true)}
        className="absolute top-1 right-2 hover:opacity-70 text-xs"
        style={{ color: '#413C04' }}
      >
        ✕
      </button>
      <p className="font-medium">📱 Save app for easy access</p>
      <p className="mt-0.5 text-xs leading-snug" style={{ color: '#413C04', opacity: 0.8 }}>
        Tap <span className="font-semibold">Share</span> →{" "}
        <span className="font-semibold">Add to Home Screen</span>
      </p>
    </div>
  );
}

/* -------------------- INSTALL BANNER -------------------- */
function InstallBanner() {
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);

  useEffect(() => {
    const onBIP = (e) => {
      e.preventDefault();
      setDeferred(e);
      setShow(true);
    };
    window.addEventListener("beforeinstallprompt", onBIP);
    return () => window.removeEventListener("beforeinstallprompt", onBIP);
  }, []);

  if (!show) return null;

  return (
    <div
      className="fixed bottom-24 left-1/2 -translate-x-1/2 z-50
                 backdrop-blur-xl text-sm px-4 py-2.5 rounded-2xl shadow-md
                 w-[90%] max-w-sm animate-fade-slide-in"
      style={{
        backgroundColor: 'rgba(255, 250, 223, 0.95)',
        border: '1px solid #E4B4A6',
        color: '#413C04'
      }}
    >
      <button
        onClick={() => setShow(false)}
        className="absolute top-1 right-2 hover:opacity-70 text-xs"
        style={{ color: '#413C04' }}
      >
        ✕
      </button>
      <div className="flex flex-col sm:flex-row items-center justify-between gap-2 text-center sm:text-left">
        <span className="font-medium">📲 Install Tour in a Box</span>
        <div className="flex items-center gap-2">
          <button
            onClick={async () => {
              await deferred.prompt();
              setShow(false);
            }}
            className="px-3 py-1 rounded-full font-semibold text-xs shadow hover:opacity-90 transition"
            style={{
              backgroundColor: '#F8662B',
              color: 'white'
            }}
          >
            Install
          </button>
          <button
            onClick={() => setShow(false)}
            className="text-xs hover:opacity-70"
            style={{ color: '#413C04', opacity: 0.7 }}
          >
            Not now
          </button>
        </div>
      </div>
    </div>
  );
}

/* -------------------- TOUR CARD -------------------- */
function TourCard({
  to,
  title,
  img,
  blurb,
  cta,
  tag,
  tagColor = "gray",
  locked,
  progress = 0,
  onStart,
}) {
  const [imageLoaded, setImageLoaded] = useState(false);
  const pct = Math.round(progress * 100);

  // Modern badge styling
  const getBadgeContent = () => {
    if (tagColor === 'green') {
      return (
        <div
          className="flex items-center gap-1.5 px-3 py-2 rounded-full backdrop-blur-lg shadow-lg"
          style={{
            backgroundColor: 'rgba(56, 91, 55, 0.95)',
            border: '1px solid rgba(56, 91, 55, 0.7)'
          }}
        >
          <CheckCircle2 size={14} className="text-white" strokeWidth={2.5} />
          <span className="text-xs font-bold text-white">{tag}</span>
        </div>
      );
    } else if (tagColor === 'yellow') {
      return (
        <div
          className="flex items-center gap-1.5 px-3 py-2 rounded-full backdrop-blur-lg shadow-lg"
          style={{
            backgroundColor: 'rgba(143, 179, 211, 0.85)',
            border: '1px solid #8FB3D3'
          }}
        >
          <ShoppingBag size={14} style={{ color: '#FFFADF' }} strokeWidth={2.5} />
          <span className="text-xs font-bold" style={{ color: '#FFFADF' }}>{tag}</span>
        </div>
      );
    } else {
      // Coming Soon
      return (
        <div
          className="flex items-center gap-1.5 px-3 py-2 rounded-full backdrop-blur-lg shadow-lg"
          style={{
            backgroundColor: 'rgba(255, 250, 223, 0.95)'
          }}
        >
          <span className="text-xs font-bold" style={{ color: '#F8662B' }}>{tag}</span>
        </div>
      );
    }
  };

  return (
    <div
      className={`group relative snap-center min-w-[82%] sm:min-w-[360px]
                   aspect-[4/5] rounded-3xl overflow-hidden bg-white
                   shadow-lg hover:shadow-2xl
                   transition-all duration-500
                   ${!locked && 'hover:-translate-y-2 active:scale-[.98]'}
                   ${locked ? "opacity-75" : ""}`}
      onClick={() => {
        if (!locked && onStart) {
          navigator.vibrate?.(10);
          onStart(to);
        }
      }}
    >
      {/* Image with loading state */}
      {!imageLoaded && (
        <div className="absolute inset-0 bg-gradient-to-br from-gray-200 to-gray-300 animate-pulse" />
      )}
      <img
        src={img}
        alt={title}
        loading="lazy"
        onLoad={() => setImageLoaded(true)}
        className={`absolute inset-0 w-full h-full object-cover
                   transition-all duration-700
                   ${imageLoaded ? 'opacity-100 scale-100' : 'opacity-0 scale-110'}
                   ${!locked && 'group-hover:scale-110'}`}
      />

      {/* Improved gradient - lighter, more modern */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />

      {/* Tag badge */}
      {tag && (
        <div className="absolute top-4 right-4">
          {getBadgeContent()}
        </div>
      )}

      {/* Download button - top left */}
      {!locked && (
        <div
          className="absolute top-4 left-4 z-10"
          onClick={(e) => e.stopPropagation()}
        >
          <OfflineDownloadButton compact assetsUrl="/offline-assets-core.json" />
        </div>
      )}

      {/* Lock overlay */}
      {locked && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/20">
          <div className="w-16 h-16 rounded-full bg-white/95 backdrop-blur-md flex items-center justify-center shadow-xl">
            <svg className="w-8 h-8 text-gray-700" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
            </svg>
          </div>
        </div>
      )}

      {/* Progress bar - if in progress but not completed */}
      {progress > 0 && progress < 1 && !locked && (
        <div className="absolute bottom-0 left-0 right-0 p-4">
          <div className="flex items-center gap-2 text-white text-xs font-semibold mb-2">
            <span>{pct}% Complete</span>
          </div>
          <div className="h-2 bg-white/20 rounded-full overflow-hidden backdrop-blur-sm">
            <div
              className="h-full rounded-full transition-all duration-500"
              style={{
                width: `${pct}%`,
                background: 'linear-gradient(to right, #8FB3D3, #385B37)'
              }}
            />
          </div>
        </div>
      )}

      {/* Content */}
      <div className={`absolute bottom-0 left-0 right-0 p-5 text-white ${progress > 0 && progress < 1 ? 'pb-20' : ''}`}>
        <h3 className="text-3xl font-bold text-white drop-shadow-lg mb-2" style={{ fontFamily: 'Rubik, sans-serif' }}>
          {title}
        </h3>
        <p className="text-sm text-white/90 leading-relaxed mb-4 line-clamp-2" style={{ fontFamily: 'Urbanist, sans-serif' }}>
          {blurb}
        </p>

        {/* CTA Button */}
        {!locked && (
          <button
            className="w-full py-3 rounded-xl font-semibold
                       shadow-lg hover:shadow-xl
                       transition-all duration-300
                       transform hover:scale-[1.02] hover:opacity-90"
            style={{
              backgroundColor: '#F8662B',
              color: 'white'
            }}
          >
            {progress === 1 ? "Play Again" : progress > 0 ? "Continue Tour →" : "Start Tour →"}
          </button>
        )}
      </div>
    </div>
  );
}

/* -------------------- HELPER FUNCTIONS -------------------- */
function getGreeting() {
  const hour = new Date().getHours();
  if (hour < 12) return "Good morning";
  if (hour < 17) return "Good afternoon";
  return "Good evening";
}

/* -------------------- BADGE EARNED MODAL -------------------- */
function BadgeEarnedModal({ tourName, onClose, onViewProfile }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{
        background: 'rgba(0, 0, 0, 0.8)',
        backdropFilter: 'blur(8px)',
      }}
      onClick={onClose}
    >
      <div
        className="max-w-md w-full rounded-2xl p-8 text-center"
        style={{
          background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
          border: '2px solid rgba(248, 102, 43, 0.3)',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <style>{`
          @keyframes badgePop {
            0% { transform: scale(0) rotate(-180deg); opacity: 0; }
            60% { transform: scale(1.2) rotate(10deg); }
            100% { transform: scale(1) rotate(0deg); opacity: 1; }
          }
          @keyframes shine {
            0%, 100% { opacity: 0.5; }
            50% { opacity: 1; }
          }
          .badge-pop {
            animation: badgePop 0.6s cubic-bezier(0.34, 1.56, 0.64, 1);
          }
          .shine {
            animation: shine 2s ease-in-out infinite;
          }
        `}</style>

        {/* Badge Icon */}
        <div className="flex items-center justify-center mb-6">
          <div
            className="badge-pop w-24 h-24 rounded-full flex items-center justify-center shine"
            style={{
              background: 'linear-gradient(135deg, #F8662B 0%, #F8662B 100%)',
              boxShadow: '0 0 40px rgba(248, 102, 43, 0.6)',
            }}
          >
            <svg
              width="48"
              height="48"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#FFF"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="8" r="7" />
              <polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88" />
            </svg>
          </div>
        </div>

        {/* Title */}
        <h2
          className="text-3xl font-bold mb-3 font-mono"
          style={{
            color: '#F8662B',
            textShadow: '0 0 20px rgba(248, 102, 43, 0.5)',
          }}
        >
          BADGE EARNED!
        </h2>

        {/* Message */}
        <p className="text-gray-300 text-base mb-2 font-mono">
          Congratulations on completing
        </p>
        <p className="text-white text-lg font-bold mb-6 font-mono">
          {tourName}!
        </p>

        <p className="text-gray-400 text-sm mb-8 font-mono">
          Your achievement badge has been added to your profile.
        </p>

        {/* Buttons */}
        <div className="flex flex-col gap-3">
          <button
            onClick={onViewProfile}
            className="w-full py-3 rounded-xl font-semibold uppercase tracking-wide text-sm transition-all"
            style={{
              background: 'linear-gradient(135deg, #F8662B 0%, #F8662B 100%)',
              color: '#ffffff',
              border: 'none',
              cursor: 'pointer',
              boxShadow: '0 4px 12px rgba(248, 102, 43, 0.3)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 6px 16px rgba(248, 102, 43, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(248, 102, 43, 0.3)';
            }}
          >
            View Profile
          </button>

          <button
            onClick={onClose}
            className="w-full py-3 rounded-xl font-medium text-sm transition-all"
            style={{
              background: 'transparent',
              color: '#9CA3AF',
              border: '1px solid rgba(156, 163, 175, 0.3)',
              cursor: 'pointer',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = 'rgba(156, 163, 175, 0.5)';
              e.currentTarget.style.color = '#D1D5DB';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = 'rgba(156, 163, 175, 0.3)';
              e.currentTarget.style.color = '#9CA3AF';
            }}
          >
            Continue
          </button>
        </div>
      </div>
    </div>
  );
}

/* -------------------- LANDING PAGE -------------------- */
export default function LandingPage() {
  const [user, setUser] = useState(null);
  const [purchasedTours, setPurchasedTours] = useState(null);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [displayName, setDisplayName] = useState("");
  const [tourProgress, setTourProgress] = useState({});
  const [showBadgeEarned, setShowBadgeEarned] = useState(false);
  const [earnedTourName, setEarnedTourName] = useState('');
  const { darkMode } = useDarkMode();
  const navigate = useNavigate();

  // Load profile name (hybrid: Firebase first, fallback to localStorage)
  useEffect(() => {
    const loadProfile = async () => {
      if (user) {
        try {
          const profile = await loadProfileHybrid(user.uid, user.email);
          if (profile) {
            setDisplayName(profile.displayName || "");
          } else {
            setDisplayName("");
          }
        } catch (err) {
          console.error("Error loading profile:", err);
          setDisplayName("");
        }
      }
    };

    loadProfile();

    // Listen for profile updates
    window.addEventListener('profileUpdated', loadProfile);
    return () => window.removeEventListener('profileUpdated', loadProfile);
  }, [user]);

  // Load tour progress from localStorage
  useEffect(() => {
    const loadTourProgress = () => {
      const progress = {};

      // San Juan tour progress
      const sanJuanKey = 'tib_progress_san-juan-cofresi-treasure';
      const sanJuanData = localStorage.getItem(sanJuanKey);
      if (sanJuanData) {
        try {
          const parsed = JSON.parse(sanJuanData);
          const totalSteps = 12; // San Juan has 12 steps
          const answeredSteps = Object.keys(parsed.answers || {}).length;
          progress['san-juan'] = answeredSteps / totalSteps;
        } catch (e) {
          progress['san-juan'] = 0;
        }
      }

      // Mexico City tour progress
      const mexicoKey = 'tib_progress_mexico-city-artifact-protocol';
      const mexicoData = localStorage.getItem(mexicoKey);
      if (mexicoData) {
        try {
          const parsed = JSON.parse(mexicoData);
          const totalSteps = 10; // Mexico City has 10 steps
          const answeredSteps = Object.keys(parsed.answers || {}).length;
          progress['mexico-city'] = answeredSteps / totalSteps;
        } catch (e) {
          progress['mexico-city'] = 0;
        }
      }

      // Chicago tour progress
      const chicagoKey = 'tib_progress_chicago';
      const chicagoData = localStorage.getItem(chicagoKey);
      if (chicagoData) {
        try {
          const parsed = JSON.parse(chicagoData);
          const totalSteps = 11; // Chicago has 11 steps
          const answeredSteps = Object.keys(parsed.answers || {}).length;
          progress['chicago'] = answeredSteps / totalSteps;
        } catch (e) {
          progress['chicago'] = 0;
        }
      }

      setTourProgress(progress);
    };

    loadTourProgress();

    // Listen for storage changes (tour progress updates)
    const handleStorageChange = () => loadTourProgress();
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('focus', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('focus', handleStorageChange);
    };
  }, []);

  // Check for badge earned on mount
  useEffect(() => {
    const badgeEarned = localStorage.getItem('badgeEarned');
    if (badgeEarned) {
      // Map tour slugs to display names
      const tourNames = {
        'san-juan-cofresi-treasure': 'San Juan',
        'mexico-city-artifact-protocol': 'Mexico City',
        'chicago': 'Chicago'
      };
      setEarnedTourName(tourNames[badgeEarned] || 'the tour');
      setShowBadgeEarned(true);
      localStorage.removeItem('badgeEarned');
    }
  }, []);

  useEffect(() => {
    const fetchAccess = async (currentUser) => {
      if (currentUser) {
        try {
          const [sanJuanAccess, mexicoCityAccess, chicagoAccess] = await Promise.all([
            checkAccess(currentUser.email, "san-juan"),
            checkAccess(currentUser.email, "mexico-city-scavenger-hunt-tour"),
            checkAccess(currentUser.email, "chicago-scavenger-hunt-tour")
          ]);
          const purchased = [];
          if (sanJuanAccess) purchased.push("san-juan");
          if (mexicoCityAccess) purchased.push("mexico-city");
          if (chicagoAccess) purchased.push("chicago");
          setPurchasedTours(purchased);
        } catch {
          setPurchasedTours([]);
        }
      } else {
        setPurchasedTours([]);
      }
    };

    const unsubscribeAuth = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      fetchAccess(currentUser);
    });

    const handleFocus = async () => {
      if (auth.currentUser) {
        // Clear access cache keys from localStorage (not sessionStorage)
        Object.keys(localStorage)
          .filter(key => key.startsWith('access:'))
          .forEach(key => localStorage.removeItem(key));
        await fetchAccess(auth.currentUser);
      }
    };
    window.addEventListener("focus", handleFocus);

    // Listen for tours-updated event (fired when user redeems a group code)
    const handleToursUpdated = async () => {
      console.log('[LandingPage] Received tours-updated event, refreshing access...');
      if (auth.currentUser) {
        await fetchAccess(auth.currentUser);
      }
    };
    window.addEventListener('tours-updated', handleToursUpdated);

    return () => {
      window.removeEventListener("focus", handleFocus);
      window.removeEventListener('tours-updated', handleToursUpdated);
      unsubscribeAuth();
    };
  }, []);

  const handleStartTour = (path) => {
    setIsTransitioning(true);
    setTimeout(() => navigate(path), 500); // duration matches fade animation
  };

  if (user && purchasedTours === null) {
    return (
      <div className="flex items-center justify-center min-h-screen text-gray-500 bg-gray-50">
        Checking your access...
      </div>
    );
  }

  // Check if tours are completed (100% progress)
  const sanJuanCompleted = tourProgress['san-juan'] === 1;
  const mexicoCityCompleted = tourProgress['mexico-city'] === 1;
  const chicagoCompleted = tourProgress['chicago'] === 1;

  const sanJuanTag = user
    ? (purchasedTours && purchasedTours.includes("san-juan"))
      ? sanJuanCompleted
        ? { text: "Completed", color: "green" }
        : { text: "Purchased", color: "green" }
      : { text: "Available for Purchase", color: "yellow" }
    : { text: "Log In to Access", color: "yellow" };
  const sanJuanLocked = !user || !(purchasedTours && purchasedTours.includes("san-juan"));

  const mexicoCityTag = user
    ? (purchasedTours && purchasedTours.includes("mexico-city"))
      ? mexicoCityCompleted
        ? { text: "Completed", color: "green" }
        : { text: "Purchased", color: "green" }
      : { text: "Available for Purchase", color: "yellow" }
    : { text: "Log In to Access", color: "yellow" };
  const mexicoCityLocked = !user || !(purchasedTours && purchasedTours.includes("mexico-city"));

  const chicagoTag = user
    ? (purchasedTours && purchasedTours.includes("chicago"))
      ? chicagoCompleted
        ? { text: "Completed", color: "green" }
        : { text: "Purchased", color: "green" }
      : { text: "Available for Purchase", color: "yellow" }
    : { text: "Log In to Access", color: "yellow" };
  const chicagoLocked = !user || !(purchasedTours && purchasedTours.includes("chicago"));

  // Build purchased and unpurchased tour arrays
  const allTours = [
    {
      id: "san-juan",
      to: "/tour/san-juan-cofresi-treasure",
      title: "San Juan, Puerto Rico",
      img: "/images/sanjuan.webp",
      blurb: "Uncover hidden clues through the colorful streets of Old San Juan.",
      tag: sanJuanTag,
      locked: sanJuanLocked,
      progress: tourProgress['san-juan'] || 0,
    },
    {
      id: "mexico-city",
      to: "/tour/mexico-city-artifact-protocol",
      title: "Mexico City, Mexico",
      img: "/images/mexicocity.webp",
      blurb: "Ancient ruins, vibrant culture, and mysteries hidden in every corner.",
      tag: mexicoCityTag,
      locked: mexicoCityLocked,
      progress: tourProgress['mexico-city'] || 0,
    },
    {
      id: "chicago",
      to: "/tour/chicago",
      title: "Chicago, Illinois",
      img: "/images/chicago.webp",
      blurb: "Navigate a tactical spy mission through downtown Chicago's iconic landmarks.",
      tag: chicagoTag,
      locked: chicagoLocked,
      progress: tourProgress['chicago'] || 0,
    }
  ];

  const purchasedToursArray = allTours.filter(tour => !tour.locked && tour.tag.color === "green");
  const unpurchasedToursArray = allTours.filter(tour => tour.locked || tour.tag.color !== "green");

  return (
    <div className={`relative min-h-screen overflow-x-hidden ${
      darkMode ? "text-gray-100" : "text-gray-800"
    }`}>
      <style>{animations}</style>

      {/* Background */}
      <div className={`fixed inset-0 -z-10 ${
        darkMode
          ? "bg-gradient-to-b from-gray-900 via-gray-800 to-gray-900"
          : ""
      }`} style={{
        background: darkMode
          ? undefined
          : 'linear-gradient(to bottom, #FFFADF 0%, rgba(255, 250, 223, 0.8) 50%, #FFFADF 100%)'
      }} />
      <div
        className="fixed inset-0 -z-10"
        style={{
          background: 'radial-gradient(circle at 50% 20%, rgba(143, 179, 211, 0.12), transparent 60%)'
        }}
      />
      <div
        className="fixed inset-0 -z-10 opacity-[.04]"
        style={{ backgroundImage: "url(/images/noise-512.png)" }}
      />

      <Header />

      <main
        className="relative z-10 px-6 pb-24"
        style={{
          marginTop: 'calc(5rem + env(safe-area-inset-top, 0px))'
        }}
      >
        {/* Personalized Greeting Section */}
        <section className="mb-8">
          {user ? (
            <>
              <h1 className={`text-3xl font-bold mb-6 ${
                darkMode ? "text-gray-100" : "text-gray-900"
              }`} style={{ fontFamily: 'Rubik, sans-serif' }}>
                {getGreeting()}, {displayName || user.email?.split('@')[0]}
              </h1>
            </>
          ) : (
            <>
              <h1 className={`text-3xl font-bold mb-1 ${
                darkMode ? "text-gray-100" : "text-gray-900"
              }`} style={{ fontFamily: 'Rubik, sans-serif' }}>
                Discover Your Next Adventure
              </h1>
              <p className={`mb-6 ${
                darkMode ? "text-gray-300" : "text-gray-600"
              }`} style={{ fontFamily: 'Urbanist, sans-serif' }}>
                Solve riddles and explore cities like never before
              </p>
            </>
          )}
        </section>

        {/* Tour Library - Only show if user has purchased tours */}
        {purchasedToursArray.length > 0 && (
          <section className="mb-12">
            <div className="mb-4">
              <h2 className={`text-2xl font-bold ${
                darkMode ? "text-gray-100" : "text-gray-900"
              }`} style={{ fontFamily: 'Rubik, sans-serif' }}>
                Tour Library
              </h2>
            </div>

            <div className="flex gap-5 overflow-x-auto no-scrollbar snap-x snap-mandatory pb-2">
              {purchasedToursArray.map((tour) => (
                <TourCard
                  key={tour.id}
                  to={tour.to}
                  title={tour.title}
                  img={tour.img}
                  blurb={tour.blurb}
                  tag={tour.tag.text}
                  tagColor={tour.tag.color}
                  locked={tour.locked}
                  progress={tour.progress}
                  onStart={handleStartTour}
                />
              ))}
            </div>
          </section>
        )}

        {/* More Adventures - Show unpurchased/locked tours */}
        {unpurchasedToursArray.length > 0 && (
          <section id="destinations">
            <h2 className={`text-2xl font-bold mb-4 ${
              darkMode ? "text-gray-100" : "text-gray-900"
            }`} style={{ fontFamily: 'Rubik, sans-serif' }}>
              More Adventures
            </h2>
            <div className="flex gap-5 overflow-x-auto no-scrollbar snap-x snap-mandatory pb-2">
              {unpurchasedToursArray.map((tour) => (
                <TourCard
                  key={tour.id}
                  to={tour.to}
                  title={tour.title}
                  img={tour.img}
                  blurb={tour.blurb}
                  tag={tour.tag.text}
                  tagColor={tour.tag.color}
                  locked={tour.locked}
                  progress={tour.progress}
                  onStart={handleStartTour}
                />
              ))}
            </div>
          </section>
        )}
      </main>

      <footer className="relative z-10 text-center text-sm text-gray-500 pb-28">
        © {new Date().getFullYear()} Tour in a Box LLC
      </footer>

      <TabBar isAuthed={!!user} />
      <InstallPrompt />
      <InstallBanner />

      {/* Fade-out overlay */}
      {isTransitioning && (
        <div className="fixed inset-0 z-[999] bg-white animate-fade-out"></div>
      )}

      {/* Badge Earned Modal */}
      {showBadgeEarned && (
        <BadgeEarnedModal
          tourName={earnedTourName}
          onClose={() => setShowBadgeEarned(false)}
          onViewProfile={() => {
            setShowBadgeEarned(false);
            navigate('/profile');
          }}
        />
      )}
    </div>
  );
}
